/**
 * What a treatment uses, and what taking it off the shelf involves.
 *
 * Added 13 October 2026. Until then a treatment only named its products; it
 * did not say how much of each, and logging a treatment never touched stock.
 * Emmanuel's test run: "when I build the treatment I should be able to list
 * the number of products and quantity of each. Even for botox I should be able
 * to say the mils or half vial." None of this is in the prototype.
 *
 * ## Stock stays whole numbers; part-units are tracked beside it
 *
 * The ledger counts packs (vials, syringes, boxes) as whole numbers, and that
 * does not change: `movements.qty` and every batch check stay integers.
 *
 * A product can say what one pack holds ("1 vial = 100 units", "1 syringe =
 * 1 ml"). A recipe asks for an amount in that unit. When a treatment needs
 * part of a pack, a whole pack is taken off the shelf and what is left over is
 * remembered as the product's OPEN amount, which the next treatment uses
 * first. That is what physically happens: a vial is opened, part is used, and
 * the rest sits in the fridge.
 *
 * ## Everything is in hundredths, as integers
 *
 * Amounts are held as hundredths of the content unit (0.5 ml is 50), for the
 * same reason money is pence: 0.1 + 0.2 must be 0.3. Two decimal places is
 * finer than anything a clinic measures.
 */
/** One whole unit, in the hundredths every amount here is held in. */
export const HUNDREDTHS = 100;
/** The largest amount a recipe line or a pack may hold: 10,000.00. */
export const MAX_HUNDREDTHS = 1_000_000;
/**
 * What one recipe line takes: the open pack first, then as many whole packs as
 * the rest needs. When the shelf cannot meet it, nothing is taken and the plan
 * says how far short it is.
 */
export function planLine(line, product) {
    const pack = Math.max(1, product.packContentsHundredths);
    const open = Math.max(0, product.openHundredths);
    const available = open + Math.max(0, product.qtyOnHand) * pack;
    const amount = line.amountHundredths;
    const base = {
        productId: product.productId,
        name: product.name,
        amountHundredths: amount,
        availableHundredths: available,
    };
    if (amount > available) {
        return {
            ...base,
            fromOpenHundredths: 0,
            packsToOpen: 0,
            openAfterHundredths: open,
            shortByHundredths: amount - available,
        };
    }
    if (amount <= open) {
        return {
            ...base,
            fromOpenHundredths: amount,
            packsToOpen: 0,
            openAfterHundredths: open - amount,
            shortByHundredths: 0,
        };
    }
    const packsToOpen = Math.ceil((amount - open) / pack);
    return {
        ...base,
        fromOpenHundredths: open,
        packsToOpen,
        openAfterHundredths: open + packsToOpen * pack - amount,
        shortByHundredths: 0,
    };
}
/**
 * What logging one treatment takes off the shelf.
 *
 * A product listed twice in a recipe is added together first: planning the two
 * lines separately would let each of them count the same open pack.
 *
 * A line whose product is not in `products` (deleted, or archived since the
 * recipe was written) is skipped, the same way availability treats it: a
 * dangling link is not a reason to refuse a treatment.
 */
export function planTreatment(recipe, products) {
    const total = new Map();
    for (const line of recipe) {
        if (line.amountHundredths <= 0)
            continue;
        total.set(line.productId, (total.get(line.productId) ?? 0) + line.amountHundredths);
    }
    const lines = [];
    for (const [productId, amountHundredths] of total) {
        const product = products.get(productId);
        if (!product)
            continue;
        lines.push(planLine({ productId, amountHundredths }, product));
    }
    const short = lines.filter((l) => l.shortByHundredths > 0);
    return { lines, ok: short.length === 0, short };
}
/**
 * What an amount of a product cost, to the nearest penny.
 *
 * `packCostPence` is what one whole pack cost; the amount is a share of it.
 * Rounded once, here, on the way out: nothing downstream multiplies it again.
 */
export function amountCostPence(amountHundredths, packCostPence, packContentsHundredths) {
    return Math.round((amountHundredths * packCostPence) / Math.max(1, packContentsHundredths));
}
/**
 * "0.5", "25", "1.25" → hundredths. Null for anything that is not a positive
 * number with at most two decimal places, so a caller can tell "left blank"
 * from "typed nonsense" from "too precise to hold".
 */
export function parseAmount(input) {
    const trimmed = input.trim();
    if (!/^\d+(\.\d{1,2})?$/.test(trimmed))
        return null;
    const [whole, frac = ''] = trimmed.split('.');
    const hundredths = Number(whole) * HUNDREDTHS + Number(frac.padEnd(2, '0'));
    if (!Number.isSafeInteger(hundredths) || hundredths <= 0 || hundredths > MAX_HUNDREDTHS) {
        return null;
    }
    return hundredths;
}
/** Hundredths → "0.5", "25", "1.25". No trailing zeros. */
export function formatAmount(hundredths) {
    const whole = Math.trunc(hundredths / HUNDREDTHS);
    const frac = Math.abs(hundredths % HUNDREDTHS);
    if (frac === 0)
        return String(whole);
    return `${whole}.${String(frac).padStart(2, '0').replace(/0$/, '')}`;
}
/**
 * Giving an amount back when a logged treatment is undone.
 *
 * The amount returns to the open pack. If that leaves at least as much open as
 * the packs this treatment opened, those packs were never touched by anything
 * else, so they go back on the shelf whole. If something else has since used
 * part of them, they stay open: a part-used vial cannot be un-opened.
 */
export function planUndo(line, product) {
    const pack = Math.max(1, product.packContentsHundredths);
    const open = Math.max(0, product.openHundredths) + line.amountHundredths;
    const packsToReturn = Math.min(line.packsOpened, Math.floor(open / pack));
    return { packsToReturn, openAfterHundredths: open - packsToReturn * pack };
}
//# sourceMappingURL=recipes.js.map